"use client";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { sanitizeChatMessageHtml } from "@/utils/sanitizeHtml";
import { trimPartialTag } from "@/services/inAppAgent/turnTimeline";
import { useSmoothText } from "@/hooks/useSmoothText";
import { CHAT_ASSISTANT_MARKDOWN_CONTENT_CLASS } from "./chatMessageChrome";
import { looksLikeAssistantHtml, trimPartialMarkdown } from "./chatAssistantFormat";
import { chatAssistantMarkdownComponents } from "./chatAssistantMarkdownComponents";

export type ChatAssistantContentProps = {
  content: string;
  className?: string;
  /** When true, reveal text gradually and trim partial markup (SSE live segments). */
  streaming?: boolean;
  /** When false, only render HTML via sanitize (pod chat legacy path). */
  enableMarkdown?: boolean;
};

function ChatAssistantContentBody({
  display,
  className,
  streaming,
  enableMarkdown,
}: {
  display: string;
  className: string;
  streaming: boolean;
  enableMarkdown: boolean;
}) {
  if (enableMarkdown && !looksLikeAssistantHtml(display)) {
    const markdown = streaming ? trimPartialMarkdown(display) : display;
    if (!markdown.trim()) {
      return null;
    }
    return (
      <div className={className} data-testid="chat-assistant-markdown">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={chatAssistantMarkdownComponents}>
          {markdown}
        </ReactMarkdown>
      </div>
    );
  }

  const html = sanitizeChatMessageHtml(streaming ? trimPartialTag(display) : display);
  if (!html.trim()) {
    return null;
  }
  return (
    <div
      className={className}
      data-testid="chat-assistant-html"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function StreamingChatAssistantContent(props: Omit<ChatAssistantContentProps, "streaming">) {
  const display = useSmoothText(props.content);
  return (
    <ChatAssistantContentBody
      display={display}
      className={props.className ?? CHAT_ASSISTANT_MARKDOWN_CONTENT_CLASS}
      streaming={true}
      enableMarkdown={props.enableMarkdown ?? true}
    />
  );
}

export function ChatAssistantContent({
  content,
  className = CHAT_ASSISTANT_MARKDOWN_CONTENT_CLASS,
  streaming = false,
  enableMarkdown = true,
}: ChatAssistantContentProps) {
  if (streaming) {
    return (
      <StreamingChatAssistantContent
        content={content}
        className={className}
        enableMarkdown={enableMarkdown}
      />
    );
  }

  return (
    <ChatAssistantContentBody
      display={content}
      className={className}
      streaming={false}
      enableMarkdown={enableMarkdown}
    />
  );
}
